/**
 * THE RULES BEHIND THE FOLLOW-UPS SCREEN, in a file with no React in it.
 *
 * `screens/Tasks.tsx` draws the list; this decides what its rows mean, and it is separate from the
 * screen because none of these rules needs a browser to be true. Four of them are the kind a wrong
 * screen gets wrong quietly, which is why each has a test beside it in `tasks.test.ts`:
 *
 *   LATE IS MEASURED AGAINST THE SERVER'S DAY AND NEVER A CLOCK. `GET /api/tasks` answers with
 *   `today` in the business zone, and every function here that has an opinion about late is handed
 *   that day. Nothing in this file reads `Date.now()`, so a phone set to another zone, or left on
 *   a table overnight, cannot make a task late a day early. A task due TODAY is not late: it is
 *   late tomorrow, which is the only reading of the word a person and this system agree on and the
 *   same one `GET /api/tasks/summary` counts.
 *
 *   AN OVERDUE TASK IS A GAP, and a gap is the one thing coral means in this interface. Nothing
 *   else on this screen is coral. A task nobody has been given is not a gap: most of them are
 *   nobody's in particular, and painting most of a list coral is how a colour stops meaning
 *   anything. `taskIsOverdue` is the only expression that decides the colour.
 *
 *   THE ORDER IS THE SERVER'S OWN, TRANSCRIBED. `api/src/routes/tasks.ts` orders open before done,
 *   then by the day it is due with the undated ones last, then oldest first, and the closed ones
 *   newest closed first, then newest created. `orderTasks` is the
 *   same rule so that filtering in the browser cannot quietly reorder a list the office reads top
 *   down, and `taskBucket` names that ladder out loud in the captions. Neither decides anything the
 *   server did not already decide.
 *
 *   AN UNTOUCHED BOX NEVER OVERWRITES A COLUMN. `taskEdits` sends only what actually changed, so
 *   saving a new date does not also write the detail back over itself, and an empty box is a
 *   cleared column ONLY where the person emptied it. That is the same rule `web/src/source.ts`
 *   keeps for the same reason: an audited write records whatever it was handed.
 */
import type { TaskKind } from '@fk/shared';
import type { NewTask, TaskPatch, TaskRow } from './api.ts';
import { daysBetween, isDay, shortDay } from './format.ts';

/** Everything the late question needs, so the helpers take a row shape and not a whole row. */
export interface DueQuestion {
  readonly state: string;
  readonly dueOn: string | null;
}

/** Everything the order needs. `TaskRow` satisfies it, and so does a row in a test. */
export interface OrderedTask extends DueQuestion {
  readonly createdAt: string;
  /** When it was finished, which places a closed row; null on an open one and on a cancel. */
  readonly completedAt?: string | null;
  /** Its last write, which stands in for `completedAt` on a cancel. */
  readonly updatedAt?: string;
}

/**
 * Late, and only the way the server counts it: still open, carrying a day, and that day behind the
 * one the server reported. A finished task is never late whatever its date said, because it was
 * done; a task with no date is not late either, because nobody ever said when.
 */
export function taskIsOverdue(task: DueQuestion, today: string): boolean {
  return task.state === 'open' && task.dueOn !== null && task.dueOn < today;
}

/** A whole number of days in words, so nothing on this screen ever reads "1 days". */
function dayWords(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * The day a follow-up is due, written for somebody reading a list.
 *
 * Both ends of it are deliberate. A day nobody set says so in words rather than leaving a blank,
 * because an empty cell and an undated task look identical and only one of them is a fact. And a
 * finished task keeps its date in the past tense: a row that reads "Due Sep 3" under the caption
 * "Finished" is a row arguing with itself.
 */
export function dueDayWords(task: DueQuestion, today: string): string {
  if (task.dueOn === null) return 'No day set';
  if (task.state !== 'open') return `Was due ${shortDay(task.dueOn)}`;
  if (!isDay(task.dueOn) || !isDay(today)) return shortDay(task.dueOn);
  if (task.dueOn < today) {
    const late = daysBetween(task.dueOn, today);
    return late === null ? `Late, was due ${shortDay(task.dueOn)}` : `${dayWords(late)} late`;
  }
  if (task.dueOn === today) return 'Due today';
  return `Due ${shortDay(task.dueOn)}`;
}

/**
 * Who it was given to. Nobody is written out rather than left blank, and it is NOT a gap: most
 * follow-ups in this business belong to whoever gets to them, and a list painted coral down its
 * whole length says nothing at all.
 */
export function whoHasItWords(assignedToName: string | null): string {
  return assignedToName ?? 'Nobody in particular';
}

/**
 * The order the office works them, which is the order the server already sent: open before done,
 * then by the day it is due with the undated ones after every dated one, then oldest first; the
 * closed ones after them newest closed first, then newest created.
 *
 * Stable, so two rows this rule cannot separate keep the order they arrived in. It exists because
 * a screen that filters rows in the browser and hands the survivors to a table has to be able to
 * say what order they are in, and re-deriving one is how a list quietly stops matching its own
 * captions.
 */
export function orderTasks<T extends OrderedTask>(rows: readonly T[]): readonly T[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const open = openRank(a.row) - openRank(b.row);
      if (open !== 0) return open;
      if (a.row.state !== 'open') {
        const closedA = closedAt(a.row);
        const closedB = closedAt(b.row);
        if (closedA !== closedB) {
          if (closedA === null) return 1;
          if (closedB === null) return -1;
          return closedA > closedB ? -1 : 1;
        }
        if (a.row.createdAt !== b.row.createdAt) return a.row.createdAt > b.row.createdAt ? -1 : 1;
        return a.index - b.index;
      }
      if (a.row.dueOn !== b.row.dueOn) {
        if (a.row.dueOn === null) return 1;
        if (b.row.dueOn === null) return -1;
        return a.row.dueOn < b.row.dueOn ? -1 : 1;
      }
      if (a.row.createdAt !== b.row.createdAt) return a.row.createdAt < b.row.createdAt ? -1 : 1;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/** The server's `coalesce(completed_at, updated_at)`: a cancel writes no `completedAt`. */
function closedAt(task: OrderedTask): string | null {
  return task.completedAt ?? task.updatedAt ?? null;
}

/** The server's own `case when state = 'open' then 0 else 1 end`, in the same two values. */
function openRank(task: DueQuestion): number {
  return task.state === 'open' ? 0 : 1;
}

/**
 * The four captions, which are that ladder read out loud. A row moves between them only because
 * the order above put it there; nothing here sorts anything.
 */
export const TASK_CAPTIONS: readonly string[] = ['Late', 'Still to do', 'No day set', 'Finished'];

export function taskBucket(task: DueQuestion, today: string): number {
  if (task.state !== 'open') return 3;
  if (taskIsOverdue(task, today)) return 0;
  return task.dueOn === null ? 2 : 1;
}

/** Rows under their caption, in the order they came, with the empty captions left out. */
export function taskGroups<T extends DueQuestion>(
  rows: readonly T[],
  today: string,
): readonly { key: string; caption: string; rows: readonly T[] }[] {
  return TASK_CAPTIONS.map((caption, index) => ({
    key: caption,
    caption,
    rows: rows.filter((row) => taskBucket(row, today) === index),
  })).filter((group) => group.rows.length > 0);
}

/* ------------------------------------------------------------------ *
 * What the boxes hold, and what a save is allowed to send.
 * ------------------------------------------------------------------ */

/**
 * The four boxes in the panel, as strings, which is what a form actually holds. The empty string
 * means the box is empty and never that a value is missing: which of those two an empty box IS
 * depends on what the row said before it, and that is `taskEdits`'s whole job.
 */
export interface TypedTask {
  readonly title: string;
  readonly detail: string;
  readonly dueOn: string;
  /** The id of the person it is given to. Empty is nobody, which is a real answer. */
  readonly assignedTo: string;
}

/** The boxes as a row fills them, so the panel opens on what is stored and not on blanks. */
export function typedTaskOf(row: TaskRow): TypedTask {
  return {
    title: row.title,
    detail: row.detail ?? '',
    dueOn: row.dueOn ?? '',
    assignedTo: row.assignedTo ?? '',
  };
}

/**
 * What is wrong with what was typed, in the sentence the person reads, or null when nothing is.
 *
 * Both of these are refused by the server too, and its sentence is what a screen shows when it
 * gets there. This exists so an obvious mistake costs no request at all, which on a phone between
 * two houses is the difference between a correction and a wait.
 */
export function taskProblem(typed: TypedTask): string | null {
  if (typed.title.trim() === '') return 'A follow-up says what has to be done.';
  if (typed.dueOn !== '' && !isDay(typed.dueOn)) return 'The day it is due is not a real date.';
  return null;
}

/**
 * ONLY WHAT CHANGED. A field that reads the same as the row is left out entirely, and a field the
 * person emptied is sent as null, which is what clears the column. Emptying the title is not one
 * of the things this may do: `taskProblem` refuses that first, and the server refuses it again.
 */
export function taskEdits(typed: TypedTask, row: TaskRow): TaskPatch {
  const title = typed.title.trim();
  const detail = typed.detail.trim();
  return {
    ...(title !== row.title && title !== '' ? { title } : {}),
    ...(detail !== (row.detail ?? '') ? { detail: detail === '' ? null : detail } : {}),
    ...(typed.dueOn !== (row.dueOn ?? '')
      ? { dueOn: typed.dueOn === '' ? null : typed.dueOn }
      : {}),
    ...(typed.assignedTo !== (row.assignedTo ?? '')
      ? { assignedTo: typed.assignedTo === '' ? null : typed.assignedTo }
      : {}),
  };
}

/**
 * A new follow-up, with every empty box left out rather than sent as an empty string. The server
 * takes null for "not known" and would take '' as a real answer, which is how a task ends up
 * given to a person whose id is the empty string.
 */
export function newTaskBody(kind: TaskKind, typed: TypedTask, clientId: string): NewTask {
  const detail = typed.detail.trim();
  return {
    kind,
    title: typed.title.trim(),
    ...(detail === '' ? {} : { detail }),
    ...(typed.dueOn === '' ? {} : { dueOn: typed.dueOn }),
    ...(typed.assignedTo === '' ? {} : { assignedTo: typed.assignedTo }),
    ...(clientId === '' ? {} : { clientId }),
  };
}

/** What a form opens on: four empty boxes. One definition, so the panel and the form agree. */
export const NOTHING_TYPED: TypedTask = Object.freeze({
  title: '',
  detail: '',
  dueOn: '',
  assignedTo: '',
});
